import {
  TelemetryEvent,
  DataField,
  cloneEvent,
  CustomerContentType,
  CustomerContentEvent,
  FilledTelemetryEvent,
  Category,
  DiagnosticLevel,
  LogLevel,
  logNotification
} from '@microsoft/oteljs';
import { addOneDSFields, CUSTOMER_CONTENT_TENANT_TOKEN, EventPropertyType } from './InternalUtils';
import { ITelemetryItem, IExtendedTelemetryItem, IEventProperty, createGuid } from '@microsoft/1ds-core-js';
import { Preprocessor, OneDSHelperProperties } from './DataModels';

const MAX_CUSTOMER_CONTENT_MAJOR_VERSION = 1;

const enum EventType {
  DiagnosticData = 0,
  CustomerContent = 1
}

interface IOneDSEventProperties {
  eventType: EventType;
  timestamp?: number;
}

/**
 * OneDSHelper helps to create a 1DS telemetry event
 *
 */
export class OneDSHelper {
  /**
   * Number of telemetry events processed
   * Also equal to the Event Sequence
   */
  public eventsProcessed: number = 0;

  /**
   * Number of telemetry events sent
   */
  public eventsSent: number = 0;

  /**
   * Number of telemetry events discarded by the 1DS SDK
   */
  public eventsDiscarded: number = 0;

  /**
   * Whether to use the Part A plugin (otherwise, fall back on appending data fields directly on the event)
   */
  protected usePartAPlugin: boolean = false;

  /**
   * Constructs an 1DS Helper
   * @param persistentDataFields - Data fields to be sent with every event; these data fields are assumed to
   * be top-level static fields, and will not be prepended with "Data."
   * @param oneDSHelperProperties - Properties on the sinks
   */
  constructor(persistentDataFields: DataField[], oneDSHelperProperties: OneDSHelperProperties) {
    // #region Private variables
    let _preprocessors: Preprocessor[] = [];
    let _optionalEventsEnabled: boolean = false;
    let _diagnosticLevelChecker: Preprocessor = {
      name: 'DiagnosticLevel',
      /**
       * Determine whether an event can be sent. Generally returns true; returns
       * false if the event does not have the appropriate event flags and the optional events override is not enabled.
       * @param telemetryEvent - An event to process
       */
      processEvent: (telemetryEvent: FilledTelemetryEvent) => {
        const diagnosticLevel = telemetryEvent.eventFlags.diagnosticLevel;
        return (
          _optionalEventsEnabled ||
          diagnosticLevel === DiagnosticLevel.Required ||
          diagnosticLevel === DiagnosticLevel.RequiredServiceData ||
          diagnosticLevel === DiagnosticLevel.RequiredServiceDataForEssentialServices
        );
      }
    };
    // These are persistent data fields kept by the OneDSHelper
    let _helperPersistentOneDSDataFields: { [name: string]: IEventProperty | string | boolean } = {};
    let _oneDSSinkSessionId = createGuid();
    let _epoch = new Date().getTime().toString();
    // #endregion

    // #region Private functions
    const preprocessEvent = (telemetryEvent: FilledTelemetryEvent) => {
      for (let i = 0; i < _preprocessors.length; i++) {
        const preprocessor = _preprocessors[i];
        if (!preprocessor.processEvent(telemetryEvent)) {
          logNotification(LogLevel.Info, Category.Sink, () => {
            return `${telemetryEvent.eventName} suppressed by ${preprocessor.name}`;
          });

          return false;
        }
      }
      return true;
    };

    /**
     * Gets a tenant token to use for sending the event
     * @param telemetryEvent - The event to be sent, which should have a tenant token in its properties
     */
    const getTenantToken = (telemetryEvent: TelemetryEvent, options: IOneDSEventProperties) => {
      if (options.eventType === EventType.CustomerContent) {
        return CUSTOMER_CONTENT_TENANT_TOKEN;
      }
      if (!telemetryEvent.telemetryProperties || !telemetryEvent.telemetryProperties.ariaTenantToken) {
        throw new Error('Missing Tenant Token');
      }
      return telemetryEvent.telemetryProperties.ariaTenantToken;
    };

    /**
     * Create a 1DS telemetry event with the Event contract and data fields
     * @param telemetryEvent - The telemetry event to be sent
     * @param options - Options for the event
     */
    const getOneDSEventInternal = (telemetryEvent: TelemetryEvent, options: IOneDSEventProperties): IExtendedTelemetryItem | undefined => {
      // Clone the event, because preprocessEvent might modify it
      const internalEvent = cloneEvent(telemetryEvent);
      if (preprocessEvent(internalEvent)) {
        this.eventsProcessed++;

        const eventTime = getTimestamp(internalEvent.timestamp);

        const data: IExtendedTelemetryItem['data'] = {
          'Event.Sequence': { value: this.eventsProcessed, propertyType: EventPropertyType.Int64 },
          'Event.Name': internalEvent.eventName,
          'Event.Source': 'OTelJS',
          'Event.Time': { value: eventTime, propertyType: EventPropertyType.DateTime }
        };
        addOneDSFields(data, internalEvent.dataFields, {
          prependDataToken: true,
          customerContentEnabled: options.eventType === EventType.CustomerContent
        });

        data['Event.Id'] = _oneDSSinkSessionId + '.' + this.eventsProcessed;
        let baseType = 'custom';

        // Event Contract
        if (internalEvent.eventContract) {
          if (internalEvent.eventContract.name) {
            data['Event.Contract'] = internalEvent.eventContract.name;
            baseType += '.' + internalEvent.eventContract.name.toLowerCase().replace(/\./g, '_');
          }
          addOneDSFields(data, internalEvent.eventContract.dataFields, {
            prependDataToken: false,
            customerContentEnabled: options.eventType === EventType.CustomerContent
          });
        }

        const oneDSEvent: ITelemetryItem = {
          iKey: getTenantToken(internalEvent, options),
          name: internalEvent.eventName,
          data,
          time: eventTime,
          baseType,
          ext: {
            sdk: {
              seq: this.eventsProcessed,
              epoch: _epoch
            }
          }
        };

        if (!this.usePartAPlugin) {
          oneDSEvent.data = { ...oneDSEvent.data, ..._helperPersistentOneDSDataFields };
        }
        return oneDSEvent;
      }
      return undefined;
    };
    // #endregion

    // #region Static private functions
    /**
     * Get a properly formatted timestamp
     * @param timestamp - telemetry event timestamp
     */
    function getTimestamp(timestamp?: number): string {
      let timestampLocal: Date;
      if (timestamp) {
        timestampLocal = new Date(timestamp);
      } else {
        timestampLocal = new Date();
      }

      return timestampLocal.toISOString();
    }
    // #endregion

    // #region Public functions
    this.getOneDSTelemetryEvent = (telemetryEvent: TelemetryEvent) => {
      return getOneDSEventInternal(telemetryEvent, {
        eventType: EventType.DiagnosticData
      });
    };

    this.getOneDSCustomerContent = (telemetryEvent: TelemetryEvent) => {
      if (
        oneDSHelperProperties.enableCustomerContent &&
        telemetryEvent.telemetryProperties!.customerContentVersion &&
        Math.floor(telemetryEvent.telemetryProperties!.customerContentVersion) <= MAX_CUSTOMER_CONTENT_MAJOR_VERSION &&
        telemetryEvent.telemetryProperties!.customerContentType === CustomerContentType.MocaAddIn
      ) {
        return getOneDSEventInternal(telemetryEvent, {
          eventType: EventType.CustomerContent
        });
      }
      return undefined;
    };

    this.addPreprocessor = (preprocessor: Preprocessor) => {
      _preprocessors.push(preprocessor);
    };

    this.addPersistentDataFields = (dataFields: DataField[]) => {
      addOneDSFields(_helperPersistentOneDSDataFields, dataFields, {
        prependDataToken: false,
        customerContentEnabled: false
      });
    };

    this.setOptionalEventsEnabled = (enabled: boolean) => {
      _optionalEventsEnabled = enabled;
    };

    this.setFullEventsEnabled = this.setOptionalEventsEnabled;
    // #endregion

    // #region Actual constructor stuff
    if (oneDSHelperProperties.enableOptionalEvents) {
      _optionalEventsEnabled = true;
    }
    this.addPersistentDataFields(persistentDataFields);
    this.addPreprocessor(_diagnosticLevelChecker);
    // #endregion
  }

  /**
   * Get an IExtendedTelemetryItem for a telemetry event, for sending via an external instance of the 1DS SDK
   * @param telemetryEvent - The telemetry event to be sent
   * @param timestamp - Time the event was sent
   */
  getOneDSTelemetryEvent: (telemetryEvent: TelemetryEvent) => IExtendedTelemetryItem | undefined;

  /**
   * Get an IExtendedTelemetryItem for a customer content event, for sending via an external instance of the 1DS SDK
   * @param customerContentEvent - The telemetry event to be sent
   * @param timestamp - Time the event was sent
   */
  getOneDSCustomerContent: (customerContentEvent: CustomerContentEvent) => IExtendedTelemetryItem | undefined;

  /**
   * Add a preprocessor, which can process events before they get sent
   * @param preprocessor - The preprocessor to add
   */
  addPreprocessor: (preprocessor: Preprocessor) => void;

  /**
   * Add data fields to be set on every event.
   * @param dataFields - Data fields to be set on every event; will not be prepended with "Data."
   */
  addPersistentDataFields: (dataFields: DataField[]) => void;

  /**
   * Enable sending optional events to Aria. On native clients, the user must explicitly allow optional events to be sent.
   * @param enabled - Whether optional events are enabled
   */
  setOptionalEventsEnabled: (enabled: boolean) => void;

  /**
   * Deprecated: Enable sending optional events to Aria. Use setOptionalEventsEnabled instead.
   * @param enabled - Whether optional events are enabled
   */
  setFullEventsEnabled: (enabled: boolean) => void;
}
